import React, { useEffect } from "react";
import { Modal, Form, Input, notification, Spin } from "antd";
import { LuMail, LuUserSquare, LuX } from "react-icons/lu";
import {
  useCreatePanelUserMutation,
  useUpdatePanelUserMutation,
} from "@/store/api/apiSlice";
import { buildCreatePanelUserPayload } from "../../pages/admin/PanelUser/helpers";

const AddPanelUserModal = ({
  visible,
  onClose,
  editData = null,
  onSuccess,
}) => {
  const [form] = Form.useForm();

  const [createPanelUser, { isLoading: isCreating }] =
    useCreatePanelUserMutation();
  const [updatePanelUser, { isLoading: isUpdating }] =
    useUpdatePanelUserMutation();

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (editData) {
      form.setFieldsValue({
        name: editData.panelUser?.name || "",
        email: editData.panelUser?.email || "",
      });
      return;
    }

    form.resetFields();
  }, [editData, visible, form]);

  const handleSubmit = async (values) => {
    const updatePayload = {
      user: {
        full_name: values.name.trim(),
        email: values.email.trim(),
      },
    };

    try {
      if (editData?._id) {
        await updatePanelUser({
          id: editData._id,
          data: updatePayload,
        }).unwrap();

        notification.success({
          message: "Success",
          description: "Panel user updated successfully!",
        });
      } else {
        await createPanelUser(
          buildCreatePanelUserPayload({
            name: values.name,
            email: values.email,
          }),
        ).unwrap();

        notification.success({
          message: "Success",
          description: "Panel user created successfully!",
        });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.data?.message ||
          `Failed to ${editData ? "update" : "create"} panel user`,
      });
    }
  };

  const loading = isCreating || isUpdating;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={520}
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      title={editData ? "Edit Panel User" : "Add Panel User"}
      className="interpreter-modal"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Full name"
          name="name"
          rules={[{ required: true, message: "Full name is required" }]}
        >
          <Input
            prefix={<LuUserSquare className="text-gray-400" />}
            placeholder="Enter full name..."
            className="h-12 rounded-2xl"
          />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Please enter a valid email address" },
          ]}
        >
          <Input
            prefix={<LuMail className="text-gray-400" />}
            placeholder="Enter email address..."
            className="h-12 rounded-2xl"
          />
        </Form.Item>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-[50%] h-14 btnSecondary border rounded-2xl text-gray-700 font-medium"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="w-[50%] h-14 bg-[#18181B] text-white rounded-2xl border-0 font-medium mt-0"
            disabled={loading}
          >
            {loading ? (
              <Spin size="small" />
            ) : editData ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddPanelUserModal;
